import { useMsal } from "@azure/msal-react";
import { Autocomplete, Button, Card, CardContent, DialogContentText, FormControlLabel, FormGroup, FormHelperText, Grid, LinearProgress, Switch, TextField } from "@mui/material";
import { cloneDeep, debounce } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Feature, PermissionContext } from "../../contexts/PermissionContext";
import { IComboBoxOption, IDispatchProps, IDispatchRequest } from "../../interfaces";
import { Values } from "../../utils";
import { FormSectionHeader } from "../Shared/FormSectionHeader";

export const DispatchDetailForm: React.FC<IDispatchProps> = ({
    dispatch,
    requireCVNumber,
    updateDispatch,
    readonly,
    allowLoanTimeAdjustment,
    ...props
}) => {

    const permissionContext = useContext(PermissionContext);
    const { accounts } = useMsal();
    const loggedInUsername = accounts[0] ? accounts[0].name ?? "" : "";

    const [dispatchModel, setDispatchModel] = useState<IDispatchRequest>(dispatch);
    const [commercialAgreements, setCommercialAgreements] = useState<IComboBoxOption[]>([]);
    const [commercialAgreementsDetails, setCommercialAgreementsDetails] = useState<IComboBoxOption[]>([]);
    const [showLoanTime, setShowLoanTime] = useState(dispatchModel.expectedReturnDate ? true : false);
    const [deliveryLoading, setDeliveryLoading] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);



    //DEBUG TEST
    const [exDate, setExDate] = useState<string>('');



    const handleDateChange = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {

        const expectedReturnDate = event.target.value;
        const currentDate = new Date();
        const inputDate = new Date(expectedReturnDate);

        const yearDiff = inputDate.getFullYear() - currentDate.getFullYear();
        const monthDiff =
            inputDate.getMonth() - currentDate.getMonth() + yearDiff * 12;

        setDispatchModel((prevModel) => {
            let newModel = cloneDeep(prevModel) as any;
            newModel["expectedReturnDate"] = expectedReturnDate;
            newModel["loanTimeYears"] = Math.floor(monthDiff / 12);
            newModel["loanTimeMonths"] = monthDiff % 12;

            handleSaveDebounced(null, newModel);

            return newModel as IDispatchRequest;
        });
    };

    const handleChange = (
        property: string,
        value: string | IComboBoxOption | number | boolean | null
    ) => {

        setUnsavedChanges(true);
        var newModel;

        setDispatchModel((prevModel) => {
            newModel = cloneDeep(prevModel) as any;
            newModel[property] = value;

            if (["loanTimeMonths", "loanTimeYears"].includes(property))
                newModel["expectedReturnDate"] = getExpectedReturnDate(
                    parseInt(newModel["loanTimeYears"] ?? "0"),
                    parseInt(newModel["loanTimeMonths"] ?? "0")
                );

            handleSaveDebounced(null, newModel);

            return newModel as IDispatchRequest;
        });

    };

    const handleSave = (e?: any, model?: IDispatchRequest | undefined) => {
        updateDispatch(cloneDeep(model ? model : dispatchModel) as IDispatchRequest);
        setUnsavedChanges(false);
    }
    const handleSaveDebounced = useRef(debounce(handleSave, 1000)).current;


    const handleCopy = () => {

        setDeliveryLoading(true);

        var newModel: IDispatchRequest;
        setDispatchModel((prevModel) => {
            newModel = {
                ...prevModel,
                deliveryRecipientCompany: prevModel.thirdPartyBusiness ?? "",
                deliveryContactName: prevModel.thirdPartyContactName ?? "",
                deliveryContactEmail: prevModel.thirdPartyContactEmail ?? "",
                deliveryContactNumber: prevModel.thirdPartyContactNumber ?? ""
            }
            updateDispatch(newModel);
            return newModel;
        });


    }

    const isLoanTimeMandatory = () => {
        if (dispatchModel.commercialAgreement?.value?.includes("Material to be loaned"))
            return true;
        return false;
    }

    const handleLoanTime = () => {
        var newValue = !showLoanTime;
        setShowLoanTime(newValue);

        let model = cloneDeep(dispatchModel) as IDispatchRequest;

        //if loan time is hidden, nullify the loan length
        if (newValue === false) {
            model.loanTimeMonths = null;
            model.loanTimeYears = null;
            model.expectedReturnDate = undefined;
        }

        setDispatchModel(model);

        handleSaveDebounced(null, model);

    }

    const getExpectedReturnDate = (years: number, months: number) => {

        const currentDate = new Date();
        const newDate = new Date(
            currentDate.getFullYear() + years,
            currentDate.getMonth() + months,
            1
        );

        const formattedDate = newDate.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).split('/').reverse().join('-');

        return formattedDate;
    }

    useEffect(() => {

        Values.setValues("commercial-agreements", setCommercialAgreements);
        Values.setValues("commercial-agreements/details", setCommercialAgreementsDetails);

    }, [])

    useEffect(() => {
        if (dispatchModel !== dispatch) {

            if (!dispatch.name) {
                dispatch.name = dispatch.name ?? loggedInUsername;
                handleSave(null, dispatch);
            } else setDispatchModel(dispatch);

        }
    }, [dispatch])



    useEffect(() => {
        if (deliveryLoading)
            setDeliveryLoading(false);
    }, [dispatchModel])

    useEffect(() => {
        setExDate(dispatchModel.expectedReturnDate?.substring(0, 10) ?? '')
    }, [dispatchModel.expectedReturnDate])

    return (
        <>
            <Card>
                <FormSectionHeader label="Dispatch Details" saving={unsavedChanges} />

                <CardContent>
                    <DialogContentText color="primary" sx={{ mb: 2 }}>
                        Porterbrook Requestor
                    </DialogContentText>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                required
                                variant="outlined"
                                label="Requestor Name"
                                value={dispatchModel.name ?? loggedInUsername}
                                onChange={(e) => handleChange("name", e.currentTarget.value)}
                                disabled={readonly}

                            />
                        </Grid>

                        {requireCVNumber === true &&
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="CV Number"
                                    value={dispatchModel.cvNumber}
                                    onChange={(e) => handleChange("cvNumber", e.currentTarget.value)}
                                    disabled={readonly}
                                    helperText="CV Number is required for material dispatched from a contract or lease"
                                />
                            </Grid>
                        }



                    </Grid>
                    <DialogContentText color="primary" sx={{ my: 2, mt: 4 }}>
                        External Third Party Requestor (If applicable)
                    </DialogContentText>
                    <Grid container spacing={2} >

                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                label="Business"
                                value={dispatchModel.thirdPartyBusiness}
                                onChange={(e) => handleChange("thirdPartyBusiness", e.currentTarget.value)}
                                onBlur={handleSave}
                                disabled={readonly}

                            />
                        </Grid>
                        <Grid item xs={4} >
                            <TextField
                                fullWidth
                                required={dispatchModel.thirdPartyBusiness ? true : false}
                                variant="outlined"
                                label="Contact Name"
                                value={dispatchModel.thirdPartyContactName}
                                onChange={(e) => handleChange("thirdPartyContactName", e.currentTarget.value)}
                                onBlur={handleSave}
                                disabled={readonly}

                            />
                        </Grid>

                        <Grid item xs={4} >
                            <TextField
                                fullWidth
                                required={dispatchModel.thirdPartyBusiness ? true : false}
                                variant="outlined"
                                label="Contact Number"
                                value={dispatchModel.thirdPartyContactNumber}
                                onChange={(e) => handleChange("thirdPartyContactNumber", e.currentTarget.value)}
                                onBlur={handleSave}
                                disabled={readonly}

                            />
                        </Grid>
                        <Grid item xs={4} >
                            <TextField
                                fullWidth
                                required={dispatchModel.thirdPartyBusiness ? true : false}
                                variant="outlined"
                                type="email"
                                label="Contact Email"
                                value={dispatchModel.thirdPartyContactEmail}
                                onChange={(e) => handleChange("thirdPartyContactEmail", e.currentTarget.value)}
                                onBlur={handleSave}
                                disabled={readonly}

                            />
                        </Grid>
                        <Grid item xs={4} >
                            <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                onClick={handleCopy}
                                disabled={readonly}
                            >
                                Copy to Delivery Details
                            </Button>
                            <FormHelperText>Copies the external requestor to delivery details</FormHelperText>
                        </Grid>
                    </Grid>


                    {permissionContext.isPermitted(Feature.DispatchRequest_CommercialAgreement) &&
                        <>
                            <Grid container spacing={2} sx={{ my: 2, mt: 4 }}>
                                <Grid item xs={8} >
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        label="Reason for Dispatch"
                                        value={dispatchModel.reasonForDispatch}
                                        onChange={(e) => handleChange("reasonForDispatch", e.currentTarget.value)}
                                        onBlur={handleSave}
                                        helperText="Please include details such as Project/HM Program/TOC/Supplier etc."
                                        disabled={readonly}
                                        required
                                        placeholder={""}
                                    />
                                </Grid>
                            </Grid>
                            <DialogContentText color="primary" sx={{ my: 2, mt: 4 }}>
                                Commercial Agreement
                            </DialogContentText>
                            <Grid container spacing={2} >
                                <Grid item xs={6}>
                                    <Autocomplete
                                        id="commercial-agreement"
                                        options={commercialAgreements.map(x => x.value)}
                                        value={dispatchModel.commercialAgreement?.value || null}
                                        disabled={readonly}
                                        onChange={(_, value) => handleChange("commercialAgreement", { value, id: commercialAgreements.filter(x => x.value === value)[0]?.id })}
                                        renderInput={(params) => <TextField
                                            {...params}
                                            label="Commercial Agreement"
                                            required
                                            helperText="Please use separate dispatch requests if commercial agreement varies per material. I.e. 1 request for sold material and 1 for loaned material"

                                        />}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        multiline
                                        rows={3}
                                        label="Agreement Details"
                                        value={dispatchModel.commercialAgreementDetails}
                                        placeholder={(commercialAgreementsDetails.filter(x => x.id === dispatchModel.commercialAgreement?.id)[0]?.value) ?? ""}
                                        onChange={(e) => handleChange("commercialAgreementDetails", e.currentTarget.value)}
                                        disabled={readonly}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormGroup>
                                        <FormControlLabel label={"Material is being returned/replaced?"} control={
                                            <Switch
                                                color="primary"
                                                checked={isLoanTimeMandatory() ? true : showLoanTime}
                                                disabled={isLoanTimeMandatory() || readonly}
                                                onChange={handleLoanTime}
                                            />
                                        } />
                                    </FormGroup>

                                </Grid>


                                {(isLoanTimeMandatory() || showLoanTime) &&
                                    <>
                                        <DialogContentText color="primary" sx={{ m: 2, mt: 4 }}>
                                            Loan Time
                                        </DialogContentText>
                                        <Grid item xs={2}>
                                            <TextField
                                                key={dispatchModel.loanTimeYears}
                                                fullWidth
                                                variant="outlined"
                                                type="number"
                                                inputProps={{
                                                    min: 0,
                                                    step: 1
                                                }}
                                                label="Years"
                                                value={dispatchModel.loanTimeYears ?? 0}
                                                onChange={(e) => handleChange("loanTimeYears", e.currentTarget.value)}
                                                disabled={allowLoanTimeAdjustment ? false : readonly}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextField
                                                key={dispatchModel.loanTimeMonths}
                                                fullWidth
                                                variant="outlined"
                                                type="number"
                                                inputProps={{ min: 0, step: 1 }}
                                                label="Months"
                                                value={dispatchModel.loanTimeMonths ?? 0}
                                                onChange={(e) => handleChange("loanTimeMonths", e.currentTarget.value)}
                                                disabled={allowLoanTimeAdjustment ? false : readonly}
                                                required
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                key={dispatchModel.expectedReturnDate}
                                                fullWidth
                                                variant="outlined"
                                                type="date"
                                                label="Expected Return Date"

                                                InputLabelProps={{ shrink: true }}
                                                value={exDate}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setExDate(e.target.value) }}
                                                onBlur={(e) => handleDateChange(e)}
                                                required
                                                disabled={allowLoanTimeAdjustment ? false : readonly}
                                            />
                                        </Grid>
                                    </>
                                }
                            </Grid>

                        </>





                    }
                </CardContent>
            </Card >

            <Card sx={{ mt: 3 }}>
                <FormSectionHeader label="Delivery Details" saving={unsavedChanges} />
                <CardContent>

                    {deliveryLoading ? <LinearProgress /> :
                        <Grid container spacing={2} marginBottom={3} >



                            <Grid item xs={12}>
                                <FormGroup>
                                    <FormControlLabel label={"Asset Protection to arrange courier?"} control={
                                        <Switch
                                            color="primary"
                                            checked={dispatchModel.projectCodeRequired === true ? true : false}
                                            disabled={readonly}
                                            onChange={(e, checked) => handleChange("projectCodeRequired", checked ? true : false)}
                                            onBlur={handleSave}

                                        />
                                    } />
                                </FormGroup>

                            </Grid>
                            {dispatchModel.projectCodeRequired === true && <>
                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        required
                                        variant="outlined"
                                        label="Project Code"
                                        value={dispatchModel.projectCode}
                                        onChange={(e) => handleChange("projectCode", e.currentTarget.value)}
                                        onBlur={handleSave}
                                        disabled={readonly}
                                        helperText="To book courier costs"
                                    />
                                </Grid>
                                <Grid xs={8} />
                            </>
                            }


                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Contact Name"
                                    value={dispatchModel.deliveryContactName}
                                    onChange={(e) => handleChange("deliveryContactName", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Recipient Company"
                                    value={dispatchModel.deliveryRecipientCompany}
                                    onChange={(e) => handleChange("deliveryRecipientCompany", e.currentTarget.value)}
                                    disabled={readonly}

                                />
                            </Grid>
                            <Grid item xs={4} />
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Address Line 1"
                                    value={dispatchModel.deliveryAddressLine1}
                                    onChange={(e) => handleChange("deliveryAddressLine1", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Address Line 2"
                                    value={dispatchModel.deliveryAddressLine2}
                                    onChange={(e) => handleChange("deliveryAddressLine2", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={4} >
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="City"
                                    value={dispatchModel.deliveryCity}
                                    onChange={(e) => handleChange("deliveryCity", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={4} >
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Postcode"
                                    value={dispatchModel.deliveryPostcode}
                                    onChange={(e) => handleChange("deliveryPostcode", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={4} >
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    label="Contact Number"
                                    value={dispatchModel.deliveryContactNumber}
                                    onChange={(e) => handleChange("deliveryContactNumber", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={4} >
                                <TextField
                                    fullWidth
                                    required
                                    variant="outlined"
                                    type="email"
                                    label="Contact Email"
                                    value={dispatchModel.deliveryContactEmail}
                                    onChange={(e) => handleChange("deliveryContactEmail", e.currentTarget.value)}
                                    disabled={readonly}
                                />
                            </Grid>
                            <Grid item xs={8} >
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    multiline
                                    rows={3}
                                    label="Opening Hours"
                                    value={dispatchModel.deliveryAddressOpeningHours}
                                    onChange={(e) => handleChange("deliveryAddressOpeningHours", e.currentTarget.value)}
                                    disabled={readonly}
                                    required
                                />
                            </Grid>
                        </Grid>
                    }
                </CardContent>
            </Card >
        </>
    );
}